"use client"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@modules/components/ui/accordion"
import { useEffect, useMemo, useState } from "react"

interface FAQSectionProps {
  items: FAQItem[]
  lng?: string
}

interface FAQItem {
  q: string
  a: string
}

export default function FAQSection({ items, lng = "en" }: FAQSectionProps) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null)

  const handleValueChange = (values: string[]) => {
    const w = window as typeof window & { dataLayer?: Record<string, unknown>[] }
    if (!w.dataLayer) return
    // Track the most recently opened item
    for (const v of values) {
      const idx = parseInt(v.replace("faq-", ""), 10)
      if (!isNaN(idx) && items[idx]) {
        w.dataLayer.push({
          event: "faq_expand",
          locale: lng,
          faqQuestion: items[idx].q,
          faqIndex: idx,
          sourceSection: "faq",
        })
      }
    }
  }

  const allValues = useMemo(() => items.map((_, i) => `faq-${i}`), [items])
  const [openValues, setOpenValues] = useState<string[]>([])

  useEffect(() => {
    const syncAccordionMode = () => {
      const nextIsMobile = window.innerWidth <= 768
      // Desktop: open just the first item as a "here's how to read this" cue.
      // Mobile: keep everything collapsed so the FAQ doesn't push later sections off-screen.
      const nextOpenValues = nextIsMobile ? [] : allValues.slice(0, 1)

      setIsMobile((current) => (current === nextIsMobile ? current : nextIsMobile))
      setOpenValues((current) => {
        if (
          current.length === nextOpenValues.length &&
          current.every((value, index) => value === nextOpenValues[index])
        ) {
          return current
        }
        return nextOpenValues
      })
    }

    syncAccordionMode()
    window.addEventListener("resize", syncAccordionMode)
    return () => window.removeEventListener("resize", syncAccordionMode)
  }, [allValues])

  if (isMobile === null) {
    return (
      <Accordion type="multiple" defaultValue={[]} className="w-full">
        {items.map((item, i) => (
          <AccordionItem key={i} value={`faq-${i}`}>
            <AccordionTrigger className="text-left text-sm md:text-base">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-xs leading-relaxed text-gray-600 md:text-sm">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    )
  }

  return (
    <Accordion
      type="multiple"
      value={openValues}
      onValueChange={(values) => {
        setOpenValues(values)
        handleValueChange(values)
      }}
      className="w-full"
    >
      {items.map((item, i) => (
        <AccordionItem key={i} value={`faq-${i}`}>
          <AccordionTrigger className="text-left text-sm md:text-base">
            {item.q}
          </AccordionTrigger>
          <AccordionContent className="text-xs leading-relaxed text-gray-600 md:text-sm">
            {item.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
